/** @jsxImportSource @emotion/react */
"use client";

import type { FunctionInterpolation, Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { TrustedCollection } from "@packages/sdk";
import {
  useRedirectToAuth,
  useRequestChallenge,
  useRequestMe,
  useRequestUsersMeSettings,
  useSession,
} from "@packages/sdk";
import * as Dialog from "@radix-ui/react-dialog";
import { differenceInDays } from "date-fns";
import {
  Fragment,
  type MouseEvent,
  useCallback,
  useMemo,
  useState,
} from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { getChallengeStatus } from "../../../../utils/content";
import type { ButtonProps } from "../../../_base/_core/Button/Button.emotion";
import { Button } from "../../../_base/_core/Button/Button.emotion";
import { Tag } from "../../../_base/_core/Tag/Tag.emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";
import { Countdown } from "../Countdown";

type JoinChallengeButtonProps = Omit<ButtonProps, "color" | "variant"> & {
  collection: Partial<TrustedCollection>;
  showSmsPromptPopup?: boolean;
  size?: "s" | "m" | "l";
  variant?: "white" | "primary" | "secondary";
};

const cssButtonDesktop: Interpolation = {
  display: "none",
  "@media (min-width: 768px)": {
    display: "flex",
  },
};
const cssButtonMobile: Interpolation<ThemeType> = (theme) => ({
  marginTop: theme.spacing.m,
  display: "flex",
  "@media (min-width: 768px)": {
    display: "none",
  },
});
const cssButtons: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.s,
});
const cssContent: Interpolation<ThemeType> = (theme) => ({
  borderRadius: theme.radius.l,
  bottom: 0,
  display: "flex",
  flexDirection: "column",
  height: "100dvh",
  margin: theme.spacing.m,
  maxHeight: "calc(75dvh - 32px)",
  maxWidth: "calc(100dvw - 32px)",
  overflowX: "hidden",
  position: "fixed",
  transform: "none",
  width: "100dvw",
  zIndex: theme.zIndices.modalContent,
  "@media (min-width: 768px)": {
    maxHeight: 655,
    maxWidth: 600,
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
  },
});
const cssContentContainer: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.onOverlayBackground,
  borderBottomLeftRadius: theme.radius.l,
  borderBottomRightRadius: theme.radius.l,
  flexGrow: 1,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  width: "100%",
});
const cssDescription: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
  marginLeft: theme.spacing.m,
  marginRight: theme.spacing.m,
});
const cssDescriptionDesktop: Interpolation = {
  display: "none",
  "@media (min-width: 768px)": {
    display: "inline",
  },
};
const cssDescriptionMobile: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
  display: "inline",
  "@media (min-width: 768px)": {
    display: "none",
  },
  marginTop: theme.spacing.s,
  textAlign: "center",
});
const cssOverlay: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.higherContrastMed,
  inset: theme.spacing.none,
  position: "fixed",
  zIndex: theme.zIndices.overlayOverMediaPlayer,
});
const cssImage: FunctionInterpolation<{ backgroundImageSrc: string }> = (
  props,
) => ({
  backgroundImage: `url(${props.backgroundImageSrc})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  position: "relative",
});
const cssImageContainer: Interpolation<ThemeType> = (theme) => ({
  borderTopLeftRadius: theme.radius.l,
  borderTopRightRadius: theme.radius.l,
  flexGrow: 1,
  height: "100%",
  width: theme.spacing.full,
});
const cssTagLeft: Interpolation<ThemeType> = (theme) => ({
  position: "absolute",
  top: theme.spacing.l,
  left: theme.spacing.l,
});
const cssTagRight: Interpolation<ThemeType> = (theme) => ({
  position: "absolute",
  top: theme.spacing.l,
  right: theme.spacing.l,
});
const cssText: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.l,
  height: "fit-content",
  padding: theme.spacing.l,
});
const cssTextInfo: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.s,
  textAlign: "center",
});

export const JoinChallengeButton = ({
  collection,
  children,
  showSmsPromptPopup = true,
  size = "m",
  variant = "white",
  ...props
}: JoinChallengeButtonProps) => {
  const { track } = useAnalytics();
  const { mutationPut: mutationPutSettings, query: querySettings } =
    useRequestUsersMeSettings({ enabled: showSmsPromptPopup });
  const { mutationPost: joinChallenge } = useRequestChallenge();
  const { query: queryMe } = useRequestMe();
  const { status } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations();
  const redirectToAuth = useRedirectToAuth();

  const isHasSeenSmsOutsideHoursConsentPopup =
    querySettings.data?.global?.has_seen_sms_outside_hours_consent_popup ===
    "true";

  const handleClose = useCallback(() => {
    setIsOpen(false);
    mutationPutSettings.mutateAsync({
      settings: [
        {
          key: "has_seen_sms_outside_hours_consent_popup",
          scope: "global",
          value: "true",
        },
      ],
    });
  }, [mutationPutSettings]);
  const handleConfirm = useCallback(async () => {
    track({
      event: HallowAnalyticsEvent.TappedRemindMeOnSMSPrompt,
    });
    handleClose();
    joinChallenge.mutate({ id: collection.id });
  }, [handleClose, joinChallenge, collection.id, track]);
  const handleOnClick = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      track({
        event: HallowAnalyticsEvent.TappedJoinChallenge,
        properties: {
          challenge: collection.id,
          collection: collection.id,
        },
      });

      if (status !== "authenticated") return redirectToAuth();

      if (
        showSmsPromptPopup &&
        !isHasSeenSmsOutsideHoursConsentPopup &&
        queryMe.data?.phone
      ) {
        track({
          event: HallowAnalyticsEvent.ViewedSMSPromptDialog,
          properties: {
            challenge: collection.id,
            screen_name: "sms_outside_hours_prompt",
          },
        });
        setIsOpen(true);
      } else joinChallenge.mutate({ id: collection.id });
    },
    [
      collection,
      joinChallenge,
      redirectToAuth,
      status,
      track,
      showSmsPromptPopup,
      isHasSeenSmsOutsideHoursConsentPopup,
      queryMe.data?.phone,
    ],
  );
  const handleNotNow = useCallback(async () => {
    track({
      event: HallowAnalyticsEvent.TappedNotNowOnSMSPrompt,
    });
    handleClose();
    joinChallenge.mutate({ id: collection.id });
  }, [handleClose, joinChallenge, collection.id, track]);

  const DaysLeft = useMemo(() => {
    const date = new Date();
    const ended = date > new Date(collection?.ends_at);
    const started = date > new Date(collection?.available_at);

    return ended ? (
      t("challenge_tile_event_over")
    ) : started ? (
      t("challenge_tile_days_left_label", {
        challenge_tile_days_left_label: differenceInDays(
          new Date(collection?.ends_at),
          date,
        ),
      })
    ) : (
      <Countdown format="default" targetDate={collection?.available_at} />
    );
  }, [collection, t]);
  const Praying = useMemo(() => {
    const fmt = Intl.NumberFormat(undefined, {
      useGrouping: true,
      maximumFractionDigits: 0,
    });
    return t("challenge_tile_praying", {
      "0": fmt.format(collection?.["users_count"] ?? 0),
    });
  }, [collection, t]);

  const challengeStatus = useMemo(
    () => getChallengeStatus(collection?.available_at, collection?.ends_at),
    [collection?.available_at, collection?.ends_at],
  );

  return (
    <Fragment>
      {isOpen && (
        <Dialog.Root open={true}>
          <Dialog.Portal>
            <Dialog.Overlay css={cssOverlay} onClick={handleClose} />
            <Dialog.Content
              autoFocus={false}
              css={cssContent}
              onOpenAutoFocus={(event) => event.preventDefault()}
            >
              <div css={cssContentContainer}>
                <div
                  css={[
                    cssImage({ backgroundImageSrc: collection?.images?.large }),
                    cssImageContainer,
                  ]}
                >
                  <Tag css={cssTagLeft} variant="lighten">
                    {Praying}
                  </Tag>
                  <Tag css={cssTagRight} variant="lighten">
                    {DaysLeft}
                  </Tag>
                </div>
                <div css={cssText}>
                  <div css={cssTextInfo}>
                    <Dialog.Title asChild>
                      <Text size="xl" type="title" as="h3">
                        <I18nMessage message="sms_prompt_popup_title" />
                      </Text>
                    </Dialog.Title>
                    <Dialog.Description asChild>
                      <Text css={cssDescription} size="l" type="body">
                        <I18nMessage message="sms_prompt_popup_subtitle" />
                        <span css={cssDescriptionDesktop}>
                          &nbsp;
                          <I18nMessage message="sms_prompt_popup_legal_web" />
                        </span>
                      </Text>
                    </Dialog.Description>
                  </div>
                  <div css={cssButtons}>
                    <Button isFullWidth onClick={handleConfirm} size="l">
                      <I18nMessage message="general_words_remind_me" />
                    </Button>
                    <Text css={cssDescriptionMobile} size="m" type="detail">
                      <I18nMessage message="sms_prompt_popup_legal" />
                    </Text>
                    <Button
                      css={cssButtonDesktop}
                      isFullWidth
                      onClick={handleNotNow}
                      size="l"
                      variant="transparentPrimary"
                    >
                      <I18nMessage message="general_words_not_now" />
                    </Button>
                  </div>
                </div>
              </div>
              <Button
                css={cssButtonMobile}
                isFullWidth
                onClick={handleNotNow}
                size="l"
                variant="transparentWhite"
              >
                <I18nMessage message="general_words_not_now" />
              </Button>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
      <Button
        disabled={joinChallenge.isPending}
        isLoading={joinChallenge.isPending}
        onClick={handleOnClick}
        size={size}
        variant={variant}
        {...props}
      >
        {children || (
          <I18nMessage
            message={
              challengeStatus === "ended"
                ? "start_plan"
                : "challenge_details_join_challenge"
            }
          />
        )}
      </Button>
    </Fragment>
  );
};
